import { ContentCopy } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';

import { useAlertsStore } from '@/configs/store/useRootStore';
import { useYaMetrika } from '@/entities/yaMetrika';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { colorStore } from '@/features/queueColors';
import { WeeklyWorkLog } from '@/types';
import { MuiColors } from '@/types/colors';
import { AnalyticsEvent } from '@/types/yametrika';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getTrackerUrl } from '@/utils/getTrackerUrl';
import { isMac } from '@/utils/isMac';

import { useLogCardMenuItems } from '../useLogCardMenuItems';

import { useCanOpenContextMenu } from './helper';

import s from './LogItem.module.scss';

type LogItemProps = {
  log: WeeklyWorkLog;
  onOpenDetails: (log: WeeklyWorkLog) => void;
};

const LogItem: React.FC<LogItemProps> = ({ log, onOpenDetails }) => {
  const canOpenContextMenu = useCanOpenContextMenu();
  const alertsStore = useAlertsStore();
  const { sendEvent } = useYaMetrika();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDelete = () => modalManagerStore.open('deleteLog', log);
  const handleEdit = () => modalManagerStore.open('editLog', log);
  const handleCopy = () => modalManagerStore.open('copyLog', { log });

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const isContextMenuClick = isMac ? e.metaKey : e.ctrlKey;

    if (isContextMenuClick && canOpenContextMenu) {
      e.preventDefault();
      setAnchorEl(e.currentTarget);

      return;
    }

    onOpenDetails(log);
  };

  const menuItems = useLogCardMenuItems({ log, handleDelete, handleEdit, handleCopy });

  const keyPrefix = log.taskKey.split('-')[0];

  const color = colorStore.getColorByPrefix(keyPrefix);
  const needsBorder = colorStore.needsBorder(keyPrefix);

  const formattedDuration = useMemo(
    () => formatDurationRussian(log.durationMinutes),
    [log.durationMinutes],
  );

  const handleCopyLink = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const response = await copyToClipboard(getTrackerUrl(log.taskKey));

    if (!response.isError) {
      alertsStore.addAlert({
        title: 'Ссылка успешно скопирована!',
        message: '',
        withReload: false,
        autoClose: true,
      });

      sendEvent(AnalyticsEvent.copyTaskName);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTaskLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    sendEvent(AnalyticsEvent.goToTask);
  };

  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <div
        key={log.id}
        className={s['log-item']}
        onClick={handleCardClick}
        onContextMenu={(e) => {
          if (!canOpenContextMenu) return;

          e.preventDefault();
          handleClick(e);
        }}
      >
        <div
          className={s['log-item__color']}
          style={{
            backgroundColor: color,
            ...(needsBorder && {
              border: '1px solid rgba(0, 0, 0, 0.2)',
              borderLeft: 'none',
            }),
          }}
        ></div>
        <div className={s['log-item__content']}>
          <div className={s['log-item__header']}>
            <div>
              <a
                onClick={handleTaskLinkClick}
                href={getTrackerUrl(log.taskKey)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color,
                  ...(needsBorder && {
                    textShadow: '0 0 1px rgba(0, 0, 0, 0.5), 0 0 1px rgba(0, 0, 0, 0.5)',
                  }),
                }}
              >
                {log.taskKey}
              </a>
              <Button
                onClick={handleCopyLink}
                sx={{ padding: 0, minWidth: '5px', marginLeft: '5px', marginBottom: '3px' }}
              >
                <ContentCopy
                  sx={{ width: '15px', height: '15px', color: MuiColors.secondaryColor }}
                />
              </Button>
            </div>
            <p className={s['log-item__title']}>{log.taskTitle}</p>
          </div>
          <div className={s['log-item__duration']}>
            <p>{formattedDuration}</p>
          </div>
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleClose();
        }}
        sx={{
          '.MuiPaper-root': {
            my: '5px',
            boxShadow: '0px 0px 10px 2px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
          },
          '.MuiMenuItem-root': {
            fontSize: '14px',
            gap: '10px',
          },
        }}
      >
        {menuItems.map(({ label, icon, action, danger }) => (
          <MenuItem
            key={label}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              action();
            }}
            sx={danger ? { color: MuiColors.dangerColor } : { color: MuiColors.primaryColor }}
          >
            {icon}
            <p>{label}</p>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default observer(LogItem);
